import { useState, useEffect } from "react";

import { Brain, ArrowLeft, Search, Edit, Trash2, Loader2, Filter, FolderOpen, Tag as TagIcon, Layers, Briefcase, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { MemoryHistory } from "@/components/memories/MemoryHistory";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import { useSupabaseQuery } from "@/hooks/useSupabaseQuery";
import { supabase } from "@/integrations/supabase/client";
import { MEMORY_CATEGORIES, MemoryCategory, getCategoryConfig } from "@/lib/memoryCategories";

interface Memory {
  id: string;
  content: string;
  version: number;
  category: MemoryCategory;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  workspace_id: string | null;
  board_id: string | null;
  folder_id: string | null;
  tag_id: string | null;
  project_id: string | null;
  boards?: { name: string };
  folders?: { name: string };
  tags?: { name: string; color: string };
  projects?: { name: string };
  workspaces?: { name: string };
}

const MemoriesOverview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Enable keyboard shortcuts for back navigation
  useKeyboardNavigation({
    onBack: () => navigate("/dashboard"),
  });
  
  const [searchQuery, setSearchQuery] = useState("");
  const [scopeFilter, setScopeFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editDialog, setEditDialog] = useState(false);
  const [editingMemory, setEditingMemory] = useState<Memory | null>(null);
  const [editContent, setEditContent] = useState("");
  const [createDialog, setCreateDialog] = useState(false);
  const [newMemoryContent, setNewMemoryContent] = useState("");
  const [newMemoryCategory, setNewMemoryCategory] = useState<MemoryCategory>("context");
  const [newMemoryScope, setNewMemoryScope] = useState<string>("workspace");
  const [newMemoryScopeId, setNewMemoryScopeId] = useState<string>("");
  const [scopeOptions, setScopeOptions] = useState<Array<{ id: string; name: string }>>([]);

  // Fetch workspace ID first
  const { data: workspaces } = useSupabaseQuery(
    ['workspaces', user?.id],
    async () => {
      const { data, error } = await supabase
        .from("workspaces")
        .select("id")
        .eq("user_id", user!.id);
      
      if (error) throw error;
      return data;
    },
    { enabled: !!user }
  );

  const workspaceId = workspaces?.[0]?.id;

  // Fetch memories using the new hook
  const { 
    data: memories = [], 
    isLoading: loading, 
    refetch: fetchMemories 
  } = useSupabaseQuery<Memory[]>(
    ['memories', workspaceId],
    async () => {
      if (!workspaceId) return [];

      const { data, error } = await supabase
        .from("memories")
        .select(`
          *,
          boards (name),
          folders (name),
          tags (name, color),
          projects (name),
          workspaces (name)
        `)
        .or(`workspace_id.eq.${workspaceId},board_id.in.(select id from boards where workspace_id='${workspaceId}'),folder_id.in.(select id from folders where workspace_id='${workspaceId}'),tag_id.in.(select id from tags where workspace_id='${workspaceId}'),project_id.in.(select id from projects where board_id in (select id from boards where workspace_id='${workspaceId}'))`)
        .order("enabled", { ascending: false })
        .order("updated_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    { enabled: !!workspaceId }
  );

  const getScope = (memory: Memory) => {
    if (memory.workspace_id) return { type: "Workspace", name: memory.workspaces?.name || "Workspace", icon: Briefcase };
    if (memory.board_id) return { type: "Board", name: memory.boards?.name || "Board", icon: Layers };
    if (memory.folder_id) return { type: "Folder", name: memory.folders?.name || "Folder", icon: FolderOpen };
    if (memory.tag_id) return { type: "Tag", name: memory.tags?.name || "Tag", icon: TagIcon, color: memory.tags?.color };
    if (memory.project_id) return { type: "Project", name: memory.projects?.name || "Project", icon: Briefcase };
    return { type: "Unknown", name: "Unknown", icon: Brain };
  };

  // Subscribe to realtime updates
  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }

    const channel = supabase
      .channel('memories-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'memories'
        },
        () => {
          fetchMemories();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, navigate, fetchMemories]);

  const filteredMemories = memories.filter((memory) => {
    const matchesSearch = memory.content.toLowerCase().includes(searchQuery.toLowerCase());
    const scope = getScope(memory);
    const matchesScope = scopeFilter === "all" || scope.type.toLowerCase() === scopeFilter.toLowerCase();
    const matchesCategory = categoryFilter === "all" || memory.category === categoryFilter;
    return matchesSearch && matchesScope && matchesCategory;
  });

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredMemories.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredMemories.map((m) => m.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    const confirmed = window.confirm(`Delete ${selectedIds.size} memories? This cannot be undone.`);
    if (!confirmed) return;

    try {
      const { error } = await supabase
        .from("memories")
        .delete()
        .in("id", Array.from(selectedIds));

      if (error) throw error;
      toast.success(`Deleted ${selectedIds.size} memories`);
      setSelectedIds(new Set());
      fetchMemories();
    } catch (error) {
      console.error("Error deleting memories:", error);
      toast.error("Failed to delete memories");
    }
  };

  const handleEdit = (memory: Memory) => {
    setEditingMemory(memory);
    setEditContent(memory.content);
    setEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editingMemory || !editContent.trim()) return;

    try {
      const { error } = await supabase
        .from("memories")
        .update({ content: editContent.trim() })
        .eq("id", editingMemory.id);

      if (error) throw error;
      toast.success("Memory updated");
      setEditDialog(false);
      fetchMemories();
    } catch (error) {
      console.error("Error updating memory:", error);
      toast.error("Failed to update memory");
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = window.confirm("Delete this memory? This cannot be undone.");
    if (!confirmed) return;

    try {
      const { error } = await supabase.from("memories").delete().eq("id", id);
      if (error) throw error;
      toast.success("Memory deleted");
      fetchMemories();
    } catch (error) {
      console.error("Error deleting memory:", error);
      toast.error("Failed to delete memory");
    }
  };

  const handleToggleEnabled = async (memoryId: string, currentEnabled: boolean) => {
    try {
      const { error } = await supabase
        .from("memories")
        .update({ enabled: !currentEnabled })
        .eq("id", memoryId);

      if (error) throw error;

      toast.success(
        currentEnabled ? "Memory disabled" : "Memory enabled"
      );

      fetchMemories();
    } catch (error) {
      console.error("Error toggling memory:", error);
      toast.error("Failed to toggle memory");
    }
  };

  const openCreateDialog = async () => {
    setNewMemoryContent("");
    setNewMemoryCategory("context");
    setNewMemoryScope("workspace");
    setNewMemoryScopeId("");
    await fetchScopeOptions("workspace");
    setCreateDialog(true);
  };

  const fetchScopeOptions = async (scope: string) => {
    try {
      const { data: workspaces } = await supabase
        .from("workspaces")
        .select("id")
        .eq("user_id", user!.id);

      if (!workspaces || workspaces.length === 0) return;
      const workspaceId = workspaces[0].id;

      let data = [];
      switch (scope) {
        case "workspace":
          data = workspaces.map(w => ({ id: w.id, name: "My Workspace" }));
          break;
        case "board":
          const { data: boards } = await supabase
            .from("boards")
            .select("id, name")
            .eq("workspace_id", workspaceId);
          data = boards || [];
          break;
        case "folder":
          const { data: folders } = await supabase
            .from("folders")
            .select("id, name")
            .eq("workspace_id", workspaceId);
          data = folders || [];
          break;
        case "tag":
          const { data: tags } = await supabase
            .from("tags")
            .select("id, name")
            .eq("workspace_id", workspaceId);
          data = tags || [];
          break;
        case "project":
          const { data: projects } = await supabase
            .from("projects")
            .select("id, name, board_id")
            .in("board_id", 
              (await supabase.from("boards").select("id").eq("workspace_id", workspaceId))
                .data?.map(b => b.id) || []
            );
          data = projects || [];
          break;
      }
      
      setScopeOptions(data);
      if (data.length > 0) {
        setNewMemoryScopeId(data[0].id);
      }
    } catch (error) {
      console.error("Error fetching scope options:", error);
    }
  };

  const handleScopeChange = (scope: string) => {
    setNewMemoryScope(scope);
    setNewMemoryScopeId("");
    fetchScopeOptions(scope);
  };

  const handleCreateMemory = async () => {
    if (!newMemoryContent.trim() || !newMemoryScopeId) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const memoryData: any = {
        content: newMemoryContent.trim(),
        category: newMemoryCategory,
        enabled: true,
      };

      // Set the appropriate scope field
      switch (newMemoryScope) {
        case "workspace":
          memoryData.workspace_id = newMemoryScopeId;
          break;
        case "board":
          memoryData.board_id = newMemoryScopeId;
          break;
        case "folder":
          memoryData.folder_id = newMemoryScopeId;
          break;
        case "tag":
          memoryData.tag_id = newMemoryScopeId;
          break;
        case "project":
          memoryData.project_id = newMemoryScopeId;
          break;
      }

      const { error } = await supabase
        .from("memories")
        .insert(memoryData);

      if (error) throw error;

      toast.success("Memory created successfully");
      setCreateDialog(false);
      fetchMemories();
    } catch (error) {
      console.error("Error creating memory:", error);
      toast.error("Failed to create memory");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <Brain className="h-5 w-5" />
              All Memories
            </h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Add Memory
            </Button>
            {selectedIds.size > 0 && (
              <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete {selectedIds.size} selected
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="mb-6 space-y-4">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search memories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={scopeFilter} onValueChange={setScopeFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by scope" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Scopes</SelectItem>
                <SelectItem value="workspace">Workspace</SelectItem>
                <SelectItem value="board">Board</SelectItem>
                <SelectItem value="folder">Folder</SelectItem>
                <SelectItem value="tag">Tag</SelectItem>
                <SelectItem value="project">Project</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(MEMORY_CATEGORIES).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" style={{ color: config.color }} />
                        {config.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {filteredMemories.length > 1 && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="select-all"
                checked={selectedIds.size === filteredMemories.length}
                onCheckedChange={toggleSelectAll}
              />
              <label htmlFor="select-all" className="text-sm cursor-pointer">
                Select all ({filteredMemories.length})
              </label>
            </div>
          )}
        </div>

        {filteredMemories.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">
              {searchQuery || scopeFilter !== "all"
                ? "No memories match your filters"
                : "No memories yet. Add context from your workspace, boards, or projects."}
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredMemories.map((memory) => {
              const scope = getScope(memory);
              const ScopeIcon = scope.icon;
              const categoryConfig = getCategoryConfig(memory.category);
              const CategoryIcon = categoryConfig.icon;
              
              return (
                <Card key={memory.id} className={`p-4 ${!memory.enabled ? "opacity-60" : ""}`}>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={selectedIds.has(memory.id)}
                      onCheckedChange={() => toggleSelection(memory.id)}
                      className="mt-1"
                    />
                    <div className="flex-1 space-y-3">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge 
                            variant="outline" 
                            className="gap-1"
                            style={{ 
                              borderColor: categoryConfig.color,
                              color: categoryConfig.color 
                            }}
                          >
                            <CategoryIcon className="h-3 w-3" />
                            {categoryConfig.label}
                          </Badge>
                          <Badge variant="outline" className="gap-1">
                            <ScopeIcon className="h-3 w-3" />
                            {scope.type}
                          </Badge>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={memory.enabled}
                              onCheckedChange={() => handleToggleEnabled(memory.id, memory.enabled)}
                            />
                            <span className="text-xs text-muted-foreground">
                              {memory.enabled ? "Enabled" : "Disabled"}
                            </span>
                          </div>
                          {scope.color ? (
                            <Badge style={{ backgroundColor: scope.color, color: "#fff" }}>
                              {scope.name}
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">{scope.name}</span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <MemoryHistory
                            memoryId={memory.id}
                            currentVersion={memory.version}
                            onRestore={(content) => {
                              setEditingMemory(memory);
                              setEditContent(content);
                              setEditDialog(true);
                            }}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(memory)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(memory.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="whitespace-pre-wrap">{memory.content}</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Memory</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              rows={6}
              placeholder="Memory content..."
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit}>Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Memory</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category</Label>
              <Select value={newMemoryCategory} onValueChange={(val) => setNewMemoryCategory(val as MemoryCategory)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(MEMORY_CATEGORIES).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" style={{ color: config.color }} />
                          {config.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Scope</Label>
              <Select value={newMemoryScope} onValueChange={handleScopeChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="workspace">
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4" />
                      Workspace
                    </div>
                  </SelectItem>
                  <SelectItem value="board">
                    <div className="flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      Board
                    </div>
                  </SelectItem>
                  <SelectItem value="folder">
                    <div className="flex items-center gap-2">
                      <FolderOpen className="h-4 w-4" />
                      Folder
                    </div>
                  </SelectItem>
                  <SelectItem value="tag">
                    <div className="flex items-center gap-2">
                      <TagIcon className="h-4 w-4" />
                      Tag
                    </div>
                  </SelectItem>
                  <SelectItem value="project">
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4" />
                      Project
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {scopeOptions.length > 0 && (
              <div>
                <Label>Select {newMemoryScope.charAt(0).toUpperCase() + newMemoryScope.slice(1)}</Label>
                <Select value={newMemoryScopeId} onValueChange={setNewMemoryScopeId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {scopeOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Content</Label>
              <Textarea
                value={newMemoryContent}
                onChange={(e) => setNewMemoryContent(e.target.value)}
                rows={6}
                placeholder="Enter memory content..."
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateMemory}>Create Memory</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemoriesOverview;
